import {
  MENS,
  WOMENS,
  UNISEX,
  GENDER,
  BRAND,
  IDEAL_FOR,
  DEALS,
  CLEARANCE,
  SALE,
  BRANDSREGEX,
  DEALSREGEX,
  GENDERS_REGEX,
  GENDERS_CATEGORY_REGEX,
  NEWARRIVALSREGEX,
  NEW,
  CLEARANCEREGEX,
  SALEREGEX,
} from './constants';
import { parseAlgoliaUrl } from '../algolia/helpers';
import brands from '../../public/brands.json';

const filterLabels: { [key: string]: string | undefined } = {
  mens: MENS,
  womens: WOMENS,
  kids: "kids'",
};

export interface SortedURLSearchParams {
  genders: string[];
  brands: string[];
  idealFor: string[];
  promotions: string[];
}

const paramsToObject = (urlSearchParams: URLSearchParams) => {
  const result: SortedURLSearchParams = {
    genders: [],
    brands: [],
    idealFor: [],
    promotions: [],
  };
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of urlSearchParams) {
    const searchParam = key.toLowerCase();
    if (searchParam.includes(GENDER)) {
      const filterLabel = filterLabels[value] || value;
      result.genders = filterLabel.split(',');
    }
    if (searchParam.includes(BRAND)) {
      result.brands = value.split(',');
    }
    if (searchParam.includes(IDEAL_FOR)) {
      result.idealFor = value.split(',').map((p) => {
        return p.toLocaleLowerCase();
      });
    }
    if (searchParam.includes('promotions')) {
      result.promotions = value.split(',').map((p) => {
        return p.toLocaleLowerCase();
      });
    }
  }
  return result;
};

export const getEnH1AndPageTitle = (url?: string, pageTitle?: string) => {
  const [path, query, ...rest] = url?.split('?') ?? [];
  if (!rest || rest?.length !== 0) {
    console.warn('url contains more than 1 "?"', { url, rest });
  }
  const queryParams = `?${query}`;
  const urlSearchParams = new URLSearchParams(queryParams);

  const objectOfParams = paramsToObject(
    urlSearchParams
  ) as unknown as SortedURLSearchParams;

  const promotionsParam = objectOfParams.promotions;

  const brandsRegex = new RegExp(BRANDSREGEX);
  const matchedBrand = path?.match(brandsRegex);
  const gendersCategoryRegex = new RegExp(GENDERS_CATEGORY_REGEX);
  const matchedGenderAndCategory = path?.match(gendersCategoryRegex);
  const gendersRegex = new RegExp(GENDERS_REGEX);
  const matchedGender = path?.match(gendersRegex);
  const dealsRegex = new RegExp(DEALSREGEX);
  const matchedDeal = path?.match(dealsRegex);
  const isAllProducts = path?.match(/products$/);
  const newArrivalsRegex = new RegExp(NEWARRIVALSREGEX);
  const matchedNewArrivals = path?.match(newArrivalsRegex);
  const clearanceRegex = new RegExp(CLEARANCEREGEX);
  const matchedClearance = path?.match(clearanceRegex);
  const saleRegex = new RegExp(SALEREGEX);
  const matchedSale = path?.match(saleRegex);

  const { brandIdentifier = '' } = parseAlgoliaUrl(path, 'en');
  const formattedBrand = brandIdentifier.replace("'", '');
  const brandName =
    brands[formattedBrand as keyof typeof brands] ?? brandIdentifier;

  let brand, gender, idealFor, badge;
  let startingPageTitle = matchedBrand
    ? undefined
    : pageTitle?.toLocaleLowerCase();

  if (pageTitle === '' && matchedGenderAndCategory) {
    // if gender in path regex extracts title as well. Sometimes title is further down the url. eg.
    // en/products/footwear/womens/c/2390 - 'footwear' is before /women
    // en/products/footwear/kids/boots/casual-boots/c/2372 - 'boots' is currently grabbed
    startingPageTitle =
      matchedGenderAndCategory[3] === 'c'
        ? matchedGenderAndCategory[1]
        : matchedGenderAndCategory[3];
  }

  // Extract Gender from the query string
  if (objectOfParams?.genders.length === 1 && !matchedGenderAndCategory) {
    gender = objectOfParams?.genders[0];
  }

  if (objectOfParams.genders.length === 2) {
    if (
      objectOfParams.genders.includes(UNISEX) &&
      objectOfParams.genders.includes(WOMENS)
    ) {
      gender = WOMENS;
    } else if (
      objectOfParams.genders.includes(UNISEX) &&
      objectOfParams.genders.includes(MENS)
    ) {
      gender = MENS;
    } else {
      gender = '';
    }
  }

  // If none was found, try to extract gender from the url path
  if (!gender && matchedGender && matchedGender.length > 0) {
    // Check for a special case: if we see kids, then we don't want to set the gender
    // Unless kids is at the end of the path
    if (matchedGender[0] !== '/kids' || path.split('/').pop() === 'kids') {
      // Check for when we have matched a gender of kids via the above logic, but the page title ALREADY has kids in it
      // In this case, we don't want to use the kids gender since it would double up in the H1
      if (
        !(
          matchedGender[0].includes('kids') &&
          (pageTitle?.includes('kids') || startingPageTitle?.includes('kids'))
        )
      ) {
        gender = matchedGender[0].replace('/', '');
        const formattedGender = filterLabels[gender];
        if (formattedGender) {
          gender = gender.replace(gender, formattedGender);
        }
      }
    }
  }

  if (objectOfParams.brands.length === 1 && !matchedBrand) {
    // if a brand filter is applied and the url isn't a specific brand like /brands/the-north-face
    brand = objectOfParams.brands[0];
  }

  if (matchedNewArrivals) {
    badge = `${NEW}:`;
  }

  if (matchedSale) {
    badge = `${SALE}:`;
  }

  if (matchedClearance) {
    badge = `${CLEARANCE}:`;
  }

  if (promotionsParam.length === 1) {
    badge = `${promotionsParam[0]}:`;
  }

  if (
    (promotionsParam.includes(CLEARANCE) && promotionsParam.includes(SALE)) ||
    matchedDeal
  ) {
    badge = DEALS;

    const dealRegex = new RegExp(`${DEALS}`, 'i');
    if (startingPageTitle?.match(dealRegex)) {
      startingPageTitle = startingPageTitle.replace(dealRegex, '').trim();
    }

    if (objectOfParams.genders.length > 0) {
      startingPageTitle = undefined;
    }
  }

  if (objectOfParams.idealFor.length === 1) {
    idealFor = `for ${objectOfParams?.idealFor[0]}`;
  }

  if (matchedBrand && brandName) {
    brand = `${brandName}`;

    if (brandName?.toLocaleLowerCase() === pageTitle?.toLocaleLowerCase()) {
      brand = pageTitle;
    }
  }

  let items = [badge, brand, gender, startingPageTitle, idealFor];
  if (badge === DEALS) {
    items = [gender, brand, startingPageTitle, badge, idealFor];
  }

  if (
    (matchedBrand || (isAllProducts && brand)) &&
    gender &&
    !startingPageTitle &&
    badge
  ) {
    items = [badge, gender, brand];

    if (badge === DEALS) {
      items = [gender, brand, badge];
    }
  }
  if ((matchedBrand || (isAllProducts && brand)) && gender && idealFor) {
    if (matchedBrand) {
      items = [gender, brand, idealFor];
    } else {
      items = [brand, gender, startingPageTitle, idealFor];
    }
  }
  if (
    (matchedBrand || (isAllProducts && brand)) &&
    brand &&
    idealFor &&
    badge === DEALS
  ) {
    items = [brand, gender, idealFor?.replace(/for/g, '').trim(), badge];
  }
  if (badge && brand && gender && idealFor) {
    items = [badge, brand, gender, startingPageTitle, idealFor];

    if (!startingPageTitle) {
      items = [badge, gender, brand, startingPageTitle, idealFor];
    }

    if (badge === DEALS) {
      items = [
        brand,
        gender,
        startingPageTitle,
        idealFor?.replace(/for/g, '').trim(),
        badge,
      ];
    }
  }

  // Handle categories being selected beneath brands, leading to pageTitle existing but not startingPageTitle
  // such as /en/products/brands/patagonia/camping-and-hiking/hiking-clothing
  // In this case, we should add the page title to the end of the H1.
  // But! We shouldn't do it when the page is just /en/products and the brand is being provided by query string
  // And, we also shouldn't do it when we are already on the brand page (i.e., the brand page is already the page title)
  if (
    brand &&
    pageTitle &&
    !startingPageTitle &&
    pageTitle.toLowerCase() !== 'all products' &&
    pageTitle !== brand
  ) {
    items.push(pageTitle);
  }

  const joinTitle = items.filter(Boolean).join(' ').trim();

  const capitalizedTitle = `${joinTitle
    .charAt(0)
    .toUpperCase()}${joinTitle.slice(1)}`;

  return capitalizedTitle;
};
